import { Option } from "@inrev/core";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import {
	createContext,
	type Dispatch,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useReducer,
} from "react";
import type {
	AdjustmentOverrideArgs,
	FinancialStatementPeriodId,
	GivenOverrideArgs,
	ViewMode,
} from "../types";
import {
	type FinancialAction,
	type FinancialField,
	type FinancialState,
	financialReducer,
	initialFinancialState,
} from "./reducer";

type PersistOverrideHandler<TArgs> = (args: TArgs) => Promise<void>;

const runPersistHandler = <TArgs,>(
	handler: PersistOverrideHandler<TArgs>,
	args: TArgs,
	onError: (args: TArgs, error: unknown) => void,
) => {
	void handler(args).catch((error: unknown) => {
		onError(args, error);
	});
};

type FinancialGridContextValue<TPeriodId extends FinancialStatementPeriodId> = {
	state: FinancialState;
	dispatch: Dispatch<FinancialAction<TPeriodId>>;
};

const FinancialGridStateContext =
	createContext<FinancialGridContextValue<FinancialStatementPeriodId> | null>(null);

type FinancialGridStateProviderProps<TPeriodId extends FinancialStatementPeriodId> = {
	initialView: ViewMode;
	children: ReactNode;
	onGivenOverride: PersistOverrideHandler<GivenOverrideArgs<TPeriodId>>;
	onAdjustmentOverride: PersistOverrideHandler<AdjustmentOverrideArgs<TPeriodId>>;
};

export const FinancialGridStateProvider = <TPeriodId extends FinancialStatementPeriodId>({
	initialView,
	children,
	onGivenOverride,
	onAdjustmentOverride,
}: FinancialGridStateProviderProps<TPeriodId>) => {
	const { showErrorFlag } = useFlags();

	const [state, rawDispatch] = useReducer(
		(state: FinancialState, action: FinancialAction<TPeriodId>) => financialReducer(state, action),
		{ view: initialView },
		initialFinancialState,
	);

	const handlePersistError = useCallback(
		(
			field: FinancialField,
			cell: { rowId: string; periodId: TPeriodId; draft: string },
			error: unknown,
		) => {
			const message = error instanceof Error ? error.message : "Failed to save changes";
			rawDispatch({
				type: "set_save_error",
				rowId: cell.rowId,
				periodId: cell.periodId,
				field,
				message,
				draft: cell.draft,
			});
			showErrorFlag({
				heading: "Error Saving Line Item",
				message,
			});
		},
		[rawDispatch, showErrorFlag],
	);

	const dispatch = useCallback<Dispatch<FinancialAction<TPeriodId>>>(
		(action) => {
			rawDispatch(action);
			if (action.type === "set_given_override") {
				const cell = { rowId: action.rowId, periodId: action.periodId, draft: action.draft };
				runPersistHandler(
					onGivenOverride,
					{ rowId: action.rowId, periodId: action.periodId, value: action.value },
					(_args, error) => handlePersistError("given", cell, error),
				);
			}
			if (action.type === "set_adjustment_override") {
				const cell = { rowId: action.rowId, periodId: action.periodId, draft: action.draft };
				const adjustmentFormula = Option.toNull(action.formula);
				runPersistHandler(
					onAdjustmentOverride,
					{ rowId: action.rowId, periodId: action.periodId, adjustment: adjustmentFormula },
					(_args, error) => handlePersistError("adjustment", cell, error),
				);
			}
		},
		[rawDispatch, onGivenOverride, onAdjustmentOverride, handlePersistError],
	);

	const value = useMemo(
		(): FinancialGridContextValue<TPeriodId> => ({ state, dispatch }),
		[state, dispatch],
	);

	return (
		<FinancialGridStateContext.Provider
			value={value as FinancialGridContextValue<FinancialStatementPeriodId>}
		>
			{children}
		</FinancialGridStateContext.Provider>
	);
};

export const useFinancialGridState = (): FinancialState => {
	const ctx = useContext(FinancialGridStateContext);
	if (!ctx) {
		throw new Error("useFinancialGridState must be used inside FinancialGridStateProvider");
	}
	return ctx.state;
};

export const useFinancialGridDispatch = <
	TPeriodId extends FinancialStatementPeriodId = FinancialStatementPeriodId,
>(): Dispatch<FinancialAction<TPeriodId>> => {
	const ctx = useContext(FinancialGridStateContext);
	if (!ctx) {
		throw new Error("useFinancialGridDispatch must be used inside FinancialGridStateProvider");
	}
	return ctx.dispatch as Dispatch<FinancialAction<TPeriodId>>;
};
